import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/routes/__root'
import { api, useAuth } from '@/lib/api'
import type {
  BaseModelDTO,
  GenerationDetailDTO,
  GenerationSubmitPayload,
  GenerationThumbnail,
  LoraPreviewDTO,
} from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { ImagePopup } from '@/components/image-popup'
import { useImagePopup, type ImageItem } from '@/hooks/use-image-popup'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { LoraSidebar } from '@/components/lora-sidebar'
import {
  createGenerationFormBase,
  type GenerationFormData,
  useGenerationFormPersistence,
} from '@/hooks/use-generation-form-persistence'

const ASPECT_RATIOS = [
  { label: '1:1', width: 1024, height: 1024 },
  { label: '4:3', width: 1024, height: 768 },
  { label: '16:9', width: 1280, height: 720 },
  { label: '3:4', width: 768, height: 1024 },
  { label: '9:16', width: 720, height: 1280 },
]

function getCurrentAspectRatio(width: number, height: number) {
  const matchingRatio = ASPECT_RATIOS.find(ratio => ratio.width === width && ratio.height === height)
  return matchingRatio || null
}

function normalizeSelectedLoras(map?: Record<number, number>): Record<number, number> {
  if (!map) return {}
  const next: Record<number, number> = {}
  for (const [key, strength] of Object.entries(map)) {
    const id = Number(key)
    if (!Number.isFinite(id) || Number.isNaN(id) || id <= 0) continue
    if (typeof strength !== 'number' || Number.isNaN(strength)) continue
    next[id] = strength
  }
  return next
}

function buildInitialFormState(
  stored: Partial<GenerationFormData>,
  model: BaseModelDTO | undefined
): GenerationFormData {
  const base = createGenerationFormBase()
  const initial: GenerationFormData = {
    ...base,
    ...stored,
    selectedLoras: normalizeSelectedLoras(stored.selectedLoras),
  }

  if (!initial.prompt) {
    initial.prompt = ''
  }

  if (stored.width === undefined) {
    initial.width = base.width
  }
  if (stored.height === undefined) {
    initial.height = base.height
  }
  if (stored.widthInput === undefined) {
    initial.widthInput = (stored.width ?? base.width).toString()
  }
  if (stored.heightInput === undefined) {
    initial.heightInput = (stored.height ?? base.height).toString()
  }
  if (stored.numImages === undefined) {
    initial.numImages = base.numImages
  }

  if (stored.seedMode !== 'fixed' && stored.seedMode !== 'random') {
    initial.seedMode = base.seedMode
  }
  if (initial.seedMode !== 'fixed') {
    initial.seed = null
  } else if (stored.seed === undefined) {
    initial.seed = base.seed
  }

  if (!initial.modelSlug && model) {
    initial.modelSlug = model.slug
  }

  if (stored.steps === undefined && model) {
    initial.steps = model.defaults.steps
  }
  if (stored.cfgScale === undefined && model) {
    initial.cfgScale = model.defaults.cfg_scale
  }
  if (stored.clipSkip === undefined && model) {
    initial.clipSkip = model.defaults.clip_skip
  }

  return initial
}

function parseLorasQuery(query: string): Record<number, number> {
  const normalized: Record<number, number> = {}
  const entries = query.split(',').map(entry => entry.trim()).filter(Boolean)
  for (const entry of entries) {
    const [idPart, strengthPart] = entry.split(':')
    const id = Number(idPart)
    const strength = Number(strengthPart)
    if (!Number.isFinite(id) || Number.isNaN(id) || id <= 0) continue
    if (!Number.isFinite(strength) || Number.isNaN(strength)) continue
    normalized[id] = strength
  }
  return normalized
}

function searchHasValues(search: ReturnType<typeof Route.useSearch>) {
  return Object.values(search).some(value => value !== undefined)
}

export const Route = createFileRoute('/generation/')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      loraId: search.loraId ? Number(search.loraId) : undefined,
      modelSlug: search.modelSlug as string | undefined,
      prompt: search.prompt as string | undefined,
      width: search.width ? Number(search.width) : undefined,
      height: search.height ? Number(search.height) : undefined,
      steps: search.steps ? Number(search.steps) : undefined,
      cfgScale: search.cfgScale ? Number(search.cfgScale) : undefined,
      clipSkip: search.clipSkip ? Number(search.clipSkip) : undefined,
      seedMode: search.seedMode as 'fixed' | 'random' | undefined,
      seed: search.seed ? Number(search.seed) : undefined,
      loras: search.loras as string | undefined,
      workflowVariant: search.workflowVariant as string | undefined,
    }
  },
  beforeLoad: async () => {
    const user = queryClient.getQueryData(['user'])
    if (!user) {
      try {
        await queryClient.ensureQueryData({ queryKey: ['user'], queryFn: api.auth.getCurrentUser })
      } catch (error) {
        throw redirect({ to: '/auth/login', search: { redirect: '/generation' } })
      }
    }
  },
  loader: () =>
    queryClient.ensureQueryData({
      queryKey: ['generation', 'onload'],
      queryFn: api.generation.onLoad,
    }),
  component: GenerationPage,
})

function GenerationPage() {
  const searchParams = Route.useSearch()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const onloadQuery = useQuery({
    queryKey: ['generation', 'onload'],
    queryFn: api.generation.onLoad,
    staleTime: 10_000,
  })

  const { data, isLoading, refetch: refetchOnload } = onloadQuery
  const queryClient = useQueryClient()

  const [selectedGenerationId, setSelectedGenerationId] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const imagePopup = useImagePopup()

  
  const queueQuery = useQuery({
    queryKey: ['queue', 'updates'],
    queryFn: api.queue.updates,
    enabled: isAuthenticated,
    refetchInterval: (query) => {
      const length = query.state.data?.queue?.length ?? 0
      return length > 0 || isGenerating ? 3000 : false
    },
    refetchOnWindowFocus: isAuthenticated,
  })
  const refetchQueue = queueQuery.refetch

  const previousQueueIdsRef = useRef<string[]>([])
  const previousRecentFirstIdRef = useRef<string | null>(null)

  const detailQuery = useQuery({
    queryKey: ['generation', 'detail', selectedGenerationId],
    queryFn: () => api.generation.detail(selectedGenerationId!, true),
    enabled: !!selectedGenerationId,
    staleTime: 0, // Always refetch when selectedGenerationId changes
  })

  useEffect(() => {
    if (!queueQuery.data) return

    const currentQueueIds = queueQuery.data.queue.map((item) => item.job_id)
    const previousQueueIds = previousQueueIdsRef.current
    const hadPreviousJobs = previousQueueIds.length > 0
    const queueShrank = hadPreviousJobs && previousQueueIds.some((id) => !currentQueueIds.includes(id))

    if (queueShrank) {
      refetchOnload().then(() => {
        // After refetching onload data, check if we have a new recent generation
        // and update the selected generation to show the newest images
        setIsGenerating(false)
      })
    } else if (isGenerating && currentQueueIds.length === 0) {
      setIsGenerating(false)
    }

    previousQueueIdsRef.current = currentQueueIds
  }, [queueQuery.data, refetchOnload, isGenerating])

  useEffect(() => {
    const recentFirstId = data?.recent?.[0]?.generation_id ?? null
    if (!recentFirstId) {
      previousRecentFirstIdRef.current = null
      return
    }

    // Always update selected generation when a new one arrives
    // or if this is the first generation we're seeing
    if (previousRecentFirstIdRef.current !== recentFirstId) {
      previousRecentFirstIdRef.current = recentFirstId
      setSelectedGenerationId(recentFirstId)
    }
  }, [data?.recent])

  // Additional effect to ensure main pane updates when new generations complete
  useEffect(() => {
    if (data?.recent?.[0]?.generation_id && !selectedGenerationId) {
      // If we have recent generations but no selected one, select the newest
      setSelectedGenerationId(data.recent[0].generation_id)
    }
  }, [data?.recent, selectedGenerationId])

  // Create a flat list of all images on the page for navigation
  const allPageImages: ImageItem[] = useMemo(() => {
    const images: ImageItem[] = []

    // Add current generation images
    const currentDetail = detailQuery.data
    if (currentDetail) {
      currentDetail.assets.forEach((asset, index) => {
        if (asset.asset_type.startsWith('image_full') || asset.asset_type.startsWith('video')) {
          images.push({
            generationId: currentDetail.generation_id,
            assetId: asset.asset_id,
            assetType: asset.asset_type,
            src: asset.asset_url ?? asset.download_url ?? undefined,
            downloadUrl: asset.download_url ?? asset.asset_url ?? undefined,
            index: asset.index ?? index,
            isVideo: asset.asset_type.startsWith('video'),
          })
        }
      })
    }

    // Add placeholders for recent and history generations
    // These will be replaced with full-resolution images when details are loaded
    const allGenerations = [...(data?.recent ?? []), ...(data?.history ?? [])]

    allGenerations.forEach((generation) => {
      // Add placeholder for each image in the generation
      const numImages = generation.thumb_urls.length
      for (let i = 0; i < numImages; i++) {
        images.push({
          generationId: generation.generation_id,
          assetId: `${generation.generation_id}-${i}`,
          assetType: 'image_full', // We'll load full images, not thumbnails
          src: undefined, // Will be loaded when details are fetched
          downloadUrl: undefined, // Will be loaded when details are fetched
          index: i,
          isVideo: false,
        })
      }
    })

    return images
  }, [data, detailQuery.data])

  const models = data?.models ?? []

  const defaultModel = useMemo(() => {
    if (!models.length) return undefined
    return models.find((model) => model.slug === 'flux-schnell') ?? models[0]
  }, [models])

  const modelBySlug = useMemo(() => {
    const map: Record<string, BaseModelDTO> = {}
    for (const model of models) {
      map[model.slug] = model
    }
    return map
  }, [models])

  const {
    isReady: isPersistenceReady,
    load: loadPersistedForm,
    save: savePersistedForm,
    clear: clearPersistedForm,
  } = useGenerationFormPersistence()
  const storedFormRef = useRef<Partial<GenerationFormData> | null>(null)
  if (storedFormRef.current === null) {
    storedFormRef.current = loadPersistedForm()
  }

  const [form, setForm] = useState<GenerationFormData>(() =>
    buildInitialFormState(storedFormRef.current ?? {}, defaultModel)
  )

  const hasAppliedInitialModelDefaults = useRef(Boolean(defaultModel))

  useEffect(() => {
    if (!defaultModel || hasAppliedInitialModelDefaults.current) {
      return
    }
    setForm(() => {
      const next = buildInitialFormState(storedFormRef.current ?? {}, defaultModel)
      hasAppliedInitialModelDefaults.current = true
      return next
    })
  }, [defaultModel])

  useEffect(() => {
    if (!isPersistenceReady) return
    savePersistedForm(form)
    storedFormRef.current = form
  }, [form, isPersistenceReady, savePersistedForm])

  const {
    modelSlug,
    prompt,
    width,
    height,
    widthInput,
    heightInput,
    numImages,
    steps,
    cfgScale,
    clipSkip,
    seedMode,
    seed,
    selectedLoras,
  } = form

  const appliedSearchRef = useRef<string | null>(null)

  useEffect(() => {
    const signature = JSON.stringify(searchParams)
    if (signature === appliedSearchRef.current) return
    appliedSearchRef.current = signature

    if (!searchHasValues(searchParams)) {
      return
    }

    setForm((prev) => {
      let mutated = false
      const next = { ...prev }

      if (searchParams.modelSlug) {
        const slug = searchParams.modelSlug
        if (next.modelSlug !== slug) {
          next.modelSlug = slug
          mutated = true
        }
        const selectedModel = modelBySlug[slug]
        if (selectedModel) {
          const targetSteps = searchParams.steps ?? selectedModel.defaults.steps
          if (next.steps !== targetSteps) {
            next.steps = targetSteps
            mutated = true
          }
          const targetCfg = searchParams.cfgScale ?? selectedModel.defaults.cfg_scale
          if (next.cfgScale !== targetCfg) {
            next.cfgScale = targetCfg
            mutated = true
          }
          const targetClip = searchParams.clipSkip ?? selectedModel.defaults.clip_skip
          if (next.clipSkip !== targetClip) {
            next.clipSkip = targetClip
            mutated = true
          }
        }
      } else {
        if (searchParams.steps !== undefined) {
          if (next.steps !== searchParams.steps) {
            next.steps = searchParams.steps
            mutated = true
          }
        }
        if (searchParams.cfgScale !== undefined) {
          if (next.cfgScale !== searchParams.cfgScale) {
            next.cfgScale = searchParams.cfgScale
            mutated = true
          }
        }
        if (searchParams.clipSkip !== undefined) {
          if (next.clipSkip !== searchParams.clipSkip) {
            next.clipSkip = searchParams.clipSkip
            mutated = true
          }
        }
      }

      if (searchParams.prompt !== undefined) {
        if (next.prompt !== searchParams.prompt) {
          next.prompt = searchParams.prompt
          mutated = true
        }
      }
      if (searchParams.width !== undefined) {
        if (next.width !== searchParams.width) {
          next.width = searchParams.width
          mutated = true
        }
        const widthString = searchParams.width.toString()
        if (next.widthInput !== widthString) {
          next.widthInput = widthString
          mutated = true
        }
      }
      if (searchParams.height !== undefined) {
        if (next.height !== searchParams.height) {
          next.height = searchParams.height
          mutated = true
        }
        const heightString = searchParams.height.toString()
        if (next.heightInput !== heightString) {
          next.heightInput = heightString
          mutated = true
        }
      }
      if (searchParams.seedMode) {
        const nextSeedMode = searchParams.seedMode
        if (next.seedMode !== nextSeedMode) {
          next.seedMode = nextSeedMode
          mutated = true
        }
        if (nextSeedMode !== 'fixed' && next.seed !== null) {
          next.seed = null
          mutated = true
        }
      }
      if (searchParams.seed !== undefined) {
        if (next.seed !== searchParams.seed) {
          next.seed = searchParams.seed
          mutated = true
        }
      }
      if (searchParams.loras !== undefined) {
        const parsedLoras = searchParams.loras ? parseLorasQuery(searchParams.loras) : {}
        const sameLoras =
          Object.keys(parsedLoras).length === Object.keys(next.selectedLoras).length &&
          Object.entries(parsedLoras).every(([id, strength]) => next.selectedLoras[Number(id)] === strength)
        if (!sameLoras) {
          next.selectedLoras = parsedLoras
          mutated = true
        }
      }

      return mutated ? next : prev
    })
  }, [modelBySlug, searchParams])

  const appliedLoraIdRef = useRef<number | null>(null)

  useEffect(() => {
    const loraId = searchParams.loraId
    if (!loraId) {
      appliedLoraIdRef.current = null
      return
    }
    if (!data?.loras) return
    if (appliedLoraIdRef.current === loraId) return

    appliedLoraIdRef.current = loraId
    const allLoras = Object.values(data.loras).flat()
    const target = allLoras.find((lora) => lora.id === loraId)
    if (!target) return

    let added = false
    setForm((prev) => {
      if (prev.selectedLoras[target.id] !== undefined) {
        return prev
      }
      added = true
      return {
        ...prev,
        selectedLoras: {
          ...prev.selectedLoras,
          [target.id]: target.default_strength ?? 1,
        },
      }
    })
    if (added) {
      toast.success(`${target.display_name} added`)
    }
  }, [data?.loras, searchParams.loraId])

  const generationMutation = useMutation({
    mutationFn: (payload: GenerationSubmitPayload) => api.generation.submit(payload),
    onSuccess: async () => {
      toast.success('Generation queued')
      setIsGenerating(true)

      // Clear query parameters after starting a new generation
      navigate({ to: '/generation', search: {
        loraId: undefined,
        modelSlug: undefined,
        prompt: undefined,
        width: undefined,
        height: undefined,
        steps: undefined,
        cfgScale: undefined,
        clipSkip: undefined,
        seedMode: undefined,
        seed: undefined,
        loras: undefined,
        workflowVariant: undefined,
      } })

      const updates: Array<Promise<unknown>> = [
        queryClient.invalidateQueries({ queryKey: ['generation', 'onload'] }),
      ]
      if (isAuthenticated) {
        updates.push(refetchQueue())
      } else {
        updates.push(queryClient.invalidateQueries({ queryKey: ['queue', 'updates'] }))
      }
      await Promise.all(updates)
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to submit generation')
      setIsGenerating(false)
    },
  })

  const handleAspectRatio = useCallback((ratio: { width: number; height: number }) => {
    setForm((prev) => {
      const newWidth = adjustToMultiple(ratio.width)
      const newHeight = adjustToMultiple(ratio.height)
      if (
        prev.width === newWidth &&
        prev.height === newHeight &&
        prev.widthInput === newWidth.toString() &&
        prev.heightInput === newHeight.toString()
      ) {
        return prev
      }
      return {
        ...prev,
        width: newWidth,
        height: newHeight,
        widthInput: newWidth.toString(),
        heightInput: newHeight.toString(),
      }
    })
  }, [])

  const handleAddLora = useCallback((lora: LoraPreviewDTO) => {
    setForm((prev) => {
      if (prev.selectedLoras[lora.id] !== undefined) {
        return prev
      }
      return {
        ...prev,
        selectedLoras: {
          ...prev.selectedLoras,
          [lora.id]: lora.default_strength ?? 1,
        },
      }
    })
  }, [])

  const handleRemoveLora = useCallback((loraId: number) => {
    setForm((prev) => {
      if (prev.selectedLoras[loraId] === undefined) return prev
      const next = { ...prev.selectedLoras }
      delete next[loraId]
      return {
        ...prev,
        selectedLoras: next,
      }
    })
  }, [])

  const handleStrengthChange = useCallback((loraId: number, strength: number) => {
    setForm((prev) => {
      if (prev.selectedLoras[loraId] === undefined) return prev
      if (prev.selectedLoras[loraId] === strength) return prev
      return {
        ...prev,
        selectedLoras: {
          ...prev.selectedLoras,
          [loraId]: strength,
        },
      }
    })
  }, [])

  const handleModelChange = useCallback(
    (slug: string) => {
      const model = modelBySlug[slug]
      setForm((prev) => {
        let mutated = false
        const next: GenerationFormData = { ...prev }

        if (prev.modelSlug !== slug) {
          next.modelSlug = slug
          mutated = true
        }

        if (model) {
          if (next.steps !== model.defaults.steps) {
            next.steps = model.defaults.steps
            mutated = true
          }
          if (next.cfgScale !== model.defaults.cfg_scale) {
            next.cfgScale = model.defaults.cfg_scale
            mutated = true
          }
          if (next.clipSkip !== model.defaults.clip_skip) {
            next.clipSkip = model.defaults.clip_skip
            mutated = true
          }
        }

        return mutated ? next : prev
      })
    },
    [modelBySlug]
  )

  const familyBySlug = useMemo(() => {
    const map: Record<string, string> = {}
    for (const model of models) {
      map[model.slug] = model.family
    }
    return map
  }, [models])

  const availableLoras: LoraPreviewDTO[] = useMemo(() => {
    if (!modelSlug || !data?.loras) return []

    const selectedFamily = familyBySlug[modelSlug]
    if (!selectedFamily) {
      return data.loras[modelSlug] ?? []
    }

    const seen = new Set<number>()
    const aggregated: LoraPreviewDTO[] = []

    for (const [slug, loras] of Object.entries(data.loras)) {
      if (familyBySlug[slug] !== selectedFamily) continue

      for (const lora of loras) {
        if (seen.has(lora.id)) continue
        seen.add(lora.id)
        aggregated.push(lora)
      }
    }

    return aggregated
  }, [data?.loras, familyBySlug, modelSlug])

  useEffect(() => {
    setForm((prev) => {
      const currentEntries = Object.entries(prev.selectedLoras)
      if (currentEntries.length === 0) {
        return prev
      }

      const validIds = new Set(availableLoras.map((lora) => lora.id))
      if (validIds.size === 0) {
        return { ...prev, selectedLoras: {} }
      }

      let mutated = false
      const nextSelected: Record<number, number> = {}

      for (const [idString, strength] of currentEntries) {
        const id = Number(idString)
        if (validIds.has(id)) {
          nextSelected[id] = strength
        } else {
          mutated = true
        }
      }

      return mutated ? { ...prev, selectedLoras: nextSelected } : prev
    })
  }, [availableLoras])

  const selectedDetail = detailQuery.data

  const handleSubmit = useCallback(() => {
    if (!modelSlug) {
      toast.error('Select a model')
      return
    }
    if (!prompt.trim()) {
      toast.error('Prompt is required')
      return
    }

    const payload: GenerationSubmitPayload = {
      prompt,
      model_slug: modelSlug,
      width,
      height,
      num_images: numImages,
      steps,
      cfg_scale: cfgScale,
      clip_skip: clipSkip,
      seed_mode: seedMode,
      seed: seedMode === 'fixed' && seed !== null ? seed : undefined,
      loras: Object.entries(selectedLoras).map(([id, strength]) => ({
        id: Number(id),
        strength,
      })),
    }
    generationMutation.mutate(payload)
  }, [
    cfgScale,
    clipSkip,
    generationMutation,
    height,
    modelSlug,
    numImages,
    prompt,
    seed,
    seedMode,
    selectedLoras,
    steps,
    width,
  ])

  const handleClearForm = useCallback(() => {
    clearPersistedForm()
    storedFormRef.current = {}
    hasAppliedInitialModelDefaults.current = Boolean(defaultModel)
    const resetState = buildInitialFormState({}, defaultModel)
    setForm(resetState)
    toast.success('Form cleared')
  }, [clearPersistedForm, defaultModel])

  const handleRegenerate = async (generationId: string) => {
    try {
      await api.generation.regenerate(generationId)
      toast.success('Image added to generation queue')
      setIsGenerating(true)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['generation', 'onload'] }),
        queryClient.invalidateQueries({ queryKey: ['queue', 'updates'] }),
      ])
    } catch (error) {
      toast.error('Failed to regenerate image')
    }
  }

  // Handle Ctrl+Enter to submit generation and arrow key navigation for main view
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault()
        handleSubmit()
      }

      // Arrow key navigation for main view images
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        // Only handle arrow keys if we have a current generation with multiple images
        if (selectedDetail && selectedDetail.assets.filter(asset => asset.asset_type.startsWith('image_full')).length > 1) {
          // Prevent default browser behavior
          event.preventDefault()

          // Dispatch a custom event that the CurrentGeneration component can listen to
          window.dispatchEvent(new CustomEvent('mainImageNavigation', {
            detail: { direction: event.key }
          }))
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleSubmit, selectedDetail])

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <aside className="border border-border bg-card/70 p-4 space-y-6">
          <section className="space-y-2">
            <Label htmlFor="prompt">Prompt</Label>
            <Textarea
              id="prompt"
              placeholder="Describe what you want to generate"
              value={prompt}
              onChange={(event) =>
                setForm((prev) =>
                  prev.prompt === event.target.value ? prev : { ...prev, prompt: event.target.value }
                )
              }
              rows={6}
            />
          </section>

          <section className="space-y-2">
            <Label>Model</Label>
            <select
              className="w-full border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              value={modelSlug ?? ''}
              onChange={(event) => handleModelChange(event.target.value)}
            >
              {!modelSlug && (
                <option value="" disabled>
                  Select a model
                </option>
              )}
              {models.map((model) => (
                <option key={model.id} value={model.slug}>
                  {model.display_name}
                </option>
              ))}
            </select>
          </section>

          <section className="space-y-3">
            <Label>Resolution</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-muted-foreground">Width</Label>
                <Input
                  type="number"
                  min={256}
                  step={16}
                  value={widthInput}
                  onChange={(event) =>
                    setForm((prev) => {
                      if (prev.widthInput === event.target.value) return prev
                      return { ...prev, widthInput: event.target.value }
                    })
                  }
                  onBlur={(event) => {
                    const value = Number(event.target.value)
                    setForm((prev) => {
                      if (!Number.isFinite(value) || value <= 0) {
                        return { ...prev, widthInput: prev.width.toString() }
                      }
                      const adjustedValue = adjustToMultiple(value)
                      if (adjustedValue === prev.width) {
                        if (prev.widthInput === adjustedValue.toString()) {
                          return prev
                        }
                        return { ...prev, widthInput: adjustedValue.toString() }
                      }
                      return {
                        ...prev,
                        width: adjustedValue,
                        widthInput: adjustedValue.toString(),
                      }
                    })
                  }}
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Height</Label>
                <Input
                  type="number"
                  min={256}
                  step={16}
                  value={heightInput}
                  onChange={(event) =>
                    setForm((prev) => {
                      if (prev.heightInput === event.target.value) return prev
                      return { ...prev, heightInput: event.target.value }
                    })
                  }
                  onBlur={(event) => {
                    const value = Number(event.target.value)
                    setForm((prev) => {
                      if (!Number.isFinite(value) || value <= 0) {
                        return { ...prev, heightInput: prev.height.toString() }
                      }
                      const adjustedValue = adjustToMultiple(value)
                      if (adjustedValue === prev.height) {
                        if (prev.heightInput === adjustedValue.toString()) {
                          return prev
                        }
                        return { ...prev, heightInput: adjustedValue.toString() }
                      }
                      return {
                        ...prev,
                        height: adjustedValue,
                        heightInput: adjustedValue.toString(),
                      }
                    })
                  }}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {ASPECT_RATIOS.map((ratio) => {
                const isActive = ratio.width === width && ratio.height === height
                return (
                  <Button
                    key={ratio.label}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleAspectRatio(ratio)}
                  >
                    {ratio.label}
                  </Button>
                )
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              Current ratio: {getCurrentAspectRatio(width, height)?.label || 'None (custom)'}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Number of images</Label>
              <span className="text-sm text-muted-foreground">{numImages}</span>
            </div>
            <Slider
              min={1}
              max={10}
              step={1}
              value={[numImages]}
              onValueChange={(value) =>
                setForm((prev) => {
                  const nextValue = value[0]
                  if (prev.numImages === nextValue) return prev
                  return { ...prev, numImages: nextValue }
                })
              }
            />
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Steps</Label>
              <span className="text-sm text-muted-foreground">{steps}</span>
            </div>
            <Slider
              min={1}
              max={60}
              step={1}
              value={[steps]}
              onValueChange={(value) =>
                setForm((prev) => {
                  const nextValue = value[0]
                  if (prev.steps === nextValue) return prev
                  return { ...prev, steps: nextValue }
                })
              }
            />

            <div className="flex items-center justify-between">
              <Label>CFG</Label>
              <span className="text-sm text-muted-foreground">{cfgScale.toFixed(1)}</span>
            </div>
            <Slider
              min={0}
              max={10}
              step={0.5}
              value={[cfgScale]}
              onValueChange={(value) =>
                setForm((prev) => {
                  const nextValue = value[0]
                  if (prev.cfgScale === nextValue) return prev
                  return { ...prev, cfgScale: nextValue }
                })
              }
            />

            <div className="flex items-center justify-between">
              <Label>Clip skip</Label>
              <span className="text-sm text-muted-foreground">{clipSkip}</span>
            </div>
            <Slider
              min={-10}
              max={-1}
              step={1}
              value={[clipSkip]}
              onValueChange={(value) =>
                setForm((prev) => {
                  const nextValue = value[0]
                  if (prev.clipSkip === nextValue) return prev
                  return { ...prev, clipSkip: nextValue }
                })
              }
            />
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Seed</Label>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={seedMode === 'fixed'}
                  onCheckedChange={(checked) =>
                    setForm((prev) => {
                      const nextMode = checked ? 'fixed' : 'random'
                      if (prev.seedMode === nextMode) return prev
                      return { ...prev, seedMode: nextMode }
                    })
                  }
                />
                <span className="text-xs text-muted-foreground">
                  {seedMode === 'fixed' ? 'Fixed' : 'Random'}
                </span>
              </div>
            </div>
            <Input
              type="number"
              placeholder="Random seed"
              value={seed ?? ''}
              disabled={seedMode !== 'fixed'}
              onChange={(event) => {
                const rawValue = event.target.value
                setForm((prev) => {
                  if (rawValue === '') {
                    if (prev.seed === null) return prev
                    return { ...prev, seed: null }
                  }
                  const parsed = Number(rawValue)
                  if (Number.isNaN(parsed)) {
                    return prev
                  }
                  if (prev.seed === parsed) return prev
                  return { ...prev, seed: parsed }
                })
              }}
            />
          </section>

          <LoraSidebar
            availableLoras={availableLoras}
            selectedLoras={selectedLoras}
            onAddLora={handleAddLora}
            onRemoveLora={handleRemoveLora}
            onUpdateStrength={handleStrengthChange}
          />

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleClearForm}
              disabled={generationMutation.isPending}
            >
              Clear
            </Button>
            <Button className="flex-1" onClick={handleSubmit} disabled={generationMutation.isPending}>
              {generationMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Generating
                </>
              ) : (
                'Generate'
              )}
            </Button>
          </div>
        </aside>

        <section className="space-y-6">
          <CurrentGeneration
            detail={selectedDetail}
            loading={detailQuery.isLoading}
            isGenerating={isGenerating}
            onOpenModal={(startIndex?: number) => {
              if (selectedDetail) {
                const images: ImageItem[] = selectedDetail.assets
                  .filter(asset => asset.asset_type.startsWith('image_full') || asset.asset_type.startsWith('video'))
                  .map((asset, index) => ({
                    generationId: selectedDetail.generation_id,
                    assetId: asset.asset_id,
                    assetType: asset.asset_type,
                    src: asset.asset_url ?? asset.download_url ?? undefined,
                    downloadUrl: asset.download_url ?? asset.asset_url ?? undefined,
                    index: asset.index ?? index,
                    isVideo: asset.asset_type.startsWith('video'),
                  }))
                imagePopup.openPopup(images, startIndex ?? 0)
              }
            }}
            onRegenerate={handleRegenerate}
            generationId={selectedDetail?.generation_id}
          />

          <HistoryGrid
            title="Recent history"
            items={[...(queueQuery.data?.recent ?? []), ...(data.history ?? [])]}
            onSelect={(generation, imageIndex) => {
              const imageIndexInAllImages = allPageImages.findIndex(
                img => img.generationId === generation.generation_id && img.index === imageIndex
              )
              if (imageIndexInAllImages !== -1) {
                imagePopup.openPopup(allPageImages, imageIndexInAllImages)
              }
            }}
            activeId={selectedGenerationId}
          />
        </section>
      </div>

      <ImagePopup
        open={imagePopup.isOpen}
        onOpenChange={imagePopup.closePopup}
        images={imagePopup.images}
        currentImageIndex={imagePopup.currentImageIndex}
        onImageIndexChange={imagePopup.setCurrentImageIndex}
        generationDetails={imagePopup.generationDetails}
        onRegenerate={imagePopup.regenerate}
        onFavorite={imagePopup.toggleFavorite}
        queryClient={queryClient}
      />
    </div>
  )
}

function adjustToMultiple(value: number, multiple = 16) {
  if (Number.isNaN(value)) return multiple
  return Math.max(multiple, Math.round(value / multiple) * multiple)
}

function CurrentGeneration({
  detail,
  loading,
  isGenerating,
  onOpenModal,
  onRegenerate,
  generationId,
}: {
  detail?: GenerationDetailDTO
  loading: boolean
  isGenerating: boolean
  onOpenModal: (startIndex?: number) => void
  onRegenerate?: (generationId: string) => void
  generationId?: string
}) {
  const imageAssets = detail?.assets.filter((asset) => asset.asset_type.startsWith('image_full')) ?? []
  const videoAssets = detail?.assets.filter((asset) => asset.asset_type.startsWith('video')) ?? []
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    setActiveIndex(0)
  }, [detail?.generation_id])

  // Handle arrow key navigation for main view
  useEffect(() => {
    const handleMainImageNavigation = (event: CustomEvent) => {
      const { direction } = event.detail

      if (direction === 'ArrowLeft') {
        setActiveIndex(prev => prev > 0 ? prev - 1 : imageAssets.length - 1)
      } else if (direction === 'ArrowRight') {
        setActiveIndex(prev => prev < imageAssets.length - 1 ? prev + 1 : 0)
      }
    }

    window.addEventListener('mainImageNavigation', handleMainImageNavigation as EventListener)
    return () => window.removeEventListener('mainImageNavigation', handleMainImageNavigation as EventListener)
  }, [imageAssets.length])

  const activeAsset = imageAssets[activeIndex]

  // Check if this generation failed (has detail but no valid assets)
  const hasFailedGeneration = detail && (
    imageAssets.length === 0 &&
    videoAssets.length === 0 &&
    detail.assets.length > 0
  )

  return (
    <div className="border border-border bg-card/70 p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Current generation</h2>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {detail?.prompt ?? 'Select a generation to view details.'}
            {loading && detail?.prompt && ' (Loading...)'}
            {hasFailedGeneration && !loading && detail?.prompt && ' (Generation failed)'}
          </p>
        </div>
        {isGenerating && !loading && detail?.prompt && (
          <div className="flex items-center text-sm text-accent">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Generating new
          </div>
        )}
      </div>

      {/* Show loading state only when loading a new generation, not when generating */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Loading generation...</span>
        </div>
      )}

      {/* Show error state when generation failed */}
      {hasFailedGeneration && !loading && (
        <div className="flex flex-col items-center justify-center py-10 space-y-4">
          <div className="text-center">
            <p className="text-sm text-destructive font-medium">Generation Failed</p>
            <p className="text-sm text-muted-foreground mt-1">
              The generation process encountered an error and no images were produced.
            </p>
          </div>
          {onRegenerate && generationId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRegenerate(generationId)}
            >
              Try Again
            </Button>
          )}
        </div>
      )}

      {/* Show existing content while generating or when not loading */}
      {!loading && !hasFailedGeneration && (activeAsset || videoAssets.length > 0) ? (
        <div className="space-y-4">
          <div className="w-full overflow-hidden border border-border bg-muted/40 flex justify-center items-center max-h-[60vh] relative">
            <button
              onClick={() => onOpenModal(activeIndex)}
              className="w-full h-full flex items-center justify-center"
              disabled={isGenerating}
            >
              {activeAsset?.asset_url ? (
                <img src={activeAsset.asset_url} alt="Generated" className="max-w-full max-h-[60vh] object-contain cursor-pointer hover:opacity-95 transition-opacity" />
              ) : videoAssets.length > 0 ? (
                <video
                  controls
                  className="w-full max-h-[60vh]"
                  src={videoAssets[0].asset_url ?? videoAssets[0].download_url ?? undefined}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-10">No preview available</p>
              )}
            </button>
          </div>
          {imageAssets.length > 1 && (
            <div className="flex space-x-2 overflow-x-auto">
              {imageAssets.map((asset, index) => (
                <button
                  key={asset.asset_id}
                  className={cn(
                    'border border-border overflow-hidden h-20 w-20 flex-shrink-0',
                    index === activeIndex ? 'border-accent' : 'border-border'
                  )}
                  onClick={() => setActiveIndex(index)}
                >
                  {asset.asset_url && (
                    <img src={asset.asset_url} alt="thumb" className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        !loading && !hasFailedGeneration && <p className="text-sm text-muted-foreground">No generation selected yet.</p>
      )}
    </div>
  )}

function HistoryGrid({
  title,
  items,
  onSelect,
  activeId,
}: {
  title: string
  items: GenerationThumbnail[]
  onSelect: (item: GenerationThumbnail, imageIndex: number) => void
  activeId: string | null
}) {
  const totalImages = items?.reduce((sum, item) => sum + (item.thumb_urls?.length || 0), 0) || 0

  return (
    <div className="border border-border bg-card/70 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">{title}</h3>
        <span className="text-xs text-muted-foreground">{totalImages} images</span>
      </div>
      <div className="grid gap-3 grid-cols-3 sm:grid-cols-4 md:grid-cols-8 lg:grid-cols-10">
        {items?.flatMap((item) =>
          (item.thumb_urls || []).map((thumb, thumbIndex) => (
            <button
              key={`${item.generation_id}-${thumbIndex}`}
              className={cn(
                'border border-border bg-card/60 text-left transition-colors hover:border-accent/60',
                activeId === item.generation_id ? 'border-accent' : 'border-border'
              )}
              onClick={() => onSelect(item, thumbIndex)}
            >
              <div className="aspect-square bg-muted/30">
                <img src={thumb} alt={item.prompt} className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <p className="text-xs font-medium line-clamp-2">{item.prompt}</p>
              </div>
            </button>
          ))
        )}
        {(!items || items.length === 0) && <p className="text-sm text-muted-foreground">No history yet</p>}
      </div>
    </div>
  )}
